/**
 * The model constants (DESIGN ED-26, ED-85; BENCH-V2-SPEC §9.5). The relay, `/api/config`, the providers, the dev route
 * and the pricing table all read them; nothing else names a model. Rewrites use the latest Sonnet (the default and the
 * pilot's choice) or, since 2026-10-12 (ED-85), the latest Haiku, chosen in the cost dialog.
 */
export const LATEST_SONNET = 'claude-sonnet-5-5';
export const LATEST_HAIKU = 'claude-haiku-5-5';

export const MODEL_ALLOWLIST = [LATEST_SONNET, LATEST_HAIKU] as const;

export type AllowedModel = (typeof MODEL_ALLOWLIST)[number];

export const DEFAULT_MODEL: AllowedModel = LATEST_SONNET;

export function isAllowedModel(model: unknown): model is AllowedModel {
	return typeof model === 'string' && (MODEL_ALLOWLIST as readonly string[]).includes(model);
}

/**
 * Minimum cacheable prompt length in tokens (Anthropic prompt-caching docs: 512 for the Claude 5.5 family). A shorter prefix
 * is processed without caching and no error is returned, so `cache_control` is only sent at or above the minimum. The live
 * measurement with `count_tokens` is L-03.
 */
export const CACHE_MIN_TOKENS: Readonly<Record<AllowedModel, number>> = {
	[LATEST_SONNET]: 512,
	[LATEST_HAIKU]: 512,
};

/** Effort levels `output_config.effort` takes on the allowlisted models (all five on Sonnet 5.5 and Haiku 5.5). */
export const EFFORT_LEVELS = ['low', 'medium', 'high', 'xhigh', 'max'] as const;
export type Effort = (typeof EFFORT_LEVELS)[number];

/**
 * The effort of the dialogue judge's calls (BENCH-V2-SPEC §6.5, v3 ED-96): `low`, whatever the run's effort. The judge answers
 * a few short boolean verdicts per chunk; T-BV2-GUARD-03 passed 20/20 at low, medium and high. Lives here, not in the web
 * adapters, so the cost model (`engine/bench/cost.ts`) can price judge calls at θ(JUDGE_EFFORT) without a dependency cycle.
 */
export const JUDGE_EFFORT: Effort = 'low';
